// src/utils/calendrier.ts — SCE Issues
// Le calendrier de choix d'une date (ui/ChoixDate, 14/10 : « améliore le
// calendrier de sélection de dates ») : grille du mois, libellés français,
// écart en clair. Pur, testable. Dates `AAAA-MM-JJ` (Jour), comme les FNC-T.

import type { Jour } from '@/types/fnct.types'
import { joursEntre } from './fnct'

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
const JOURS_COURTS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.']
/** En-têtes de colonnes, semaine du lundi. */
export const INITIALES_JOURS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'] as const

const midi = (j: Jour): Date => new Date(`${j}T12:00:00Z`)
const enJour = (d: Date): Jour => d.toISOString().slice(0, 10)

/** Le jour de la semaine, lundi = 0 … dimanche = 6. */
export function rangDansSemaine(j: Jour): number {
  return (midi(j).getUTCDay() + 6) % 7
}

export function decalerJours(j: Jour, n: number): Jour {
  const d = midi(j)
  d.setUTCDate(d.getUTCDate() + n)
  return enJour(d)
}

/** Le même jour, n mois plus tard (ramené au dernier jour du mois s'il n'existe pas). */
export function decalerMois(j: Jour, n: number): Jour {
  const d = midi(j)
  const jour = d.getUTCDate()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() + n)
  const dernier = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 12)).getUTCDate()
  d.setUTCDate(Math.min(jour, dernier))
  return enJour(d)
}

/** Premier jour du mois d'une date. */
export function debutMois(j: Jour): Jour {
  return `${j.slice(0, 7)}-01`
}

/** Les 42 jours (6 semaines, du lundi) qui affichent le mois de `j`. */
export function grilleMois(j: Jour): Jour[] {
  const premier = debutMois(j)
  const depart = decalerJours(premier, -rangDansSemaine(premier))
  return Array.from({ length: 42 }, (_, i) => decalerJours(depart, i))
}

/** « octobre 2026 ». */
export function titreMois(j: Jour): string {
  return `${MOIS[Number(j.slice(5, 7)) - 1]} ${j.slice(0, 4)}`
}

/** « mer. 28 oct. 2026 ». */
export function libelleDate(j: Jour): string {
  const d = midi(j)
  return `${JOURS_COURTS[d.getUTCDay()]} ${d.getUTCDate()} ${MOIS_COURTS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/** « aujourd'hui », « demain », « dans 10 jours », « hier », « il y a 3 jours ». */
export function ecartLisible(aujourdhui: Jour, j: Jour): string {
  const n = joursEntre(aujourdhui, j)
  if (n === 0) return 'aujourd’hui'
  if (n === 1) return 'demain'
  if (n === -1) return 'hier'
  return n > 0 ? `dans ${n} jours` : `il y a ${-n} jours`
}

/** Hors des bornes (`min`, `max` compris) ? */
export function horsBornes(j: Jour, min?: Jour | null, max?: Jour | null): boolean {
  return Boolean((min && j < min) || (max && j > max))
}
